import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { UnitLink } from '@/entities/fleet'
import {
  LeaseRow,
  listLineLeasesQueryKey,
  releaseLineLeaseMutation,
  type TelephonyLeaseView,
  useLineLeases,
} from '@/entities/telephony'
import { errorMessage } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { ConfirmButton } from '@/shared/ui/confirm-button'
import { EmptyState } from '@/shared/ui/empty-state'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'

export const Route = createFileRoute('/telephony/leases')({
  component: LeasesPage,
})

function LeasesPage() {
  const { leases, isPending, isError, error, refetch } = useLineLeases()

  return (
    <>
      <PageHeader
        title="Аренды линий"
        description="Привязка учёток к юнитам: слив и освобождение"
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить аренды"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {leases.length === 0 ? (
            <EmptyState
              icon="link"
              title="Аренд нет"
              description="Аренды создаёт бэкенд, когда ячейка берёт линии под пул"
            />
          ) : (
            <div className="flex flex-col gap-2">
              {leases.map((lease) => (
                <ReleasableLease key={lease.id} lease={lease} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}

function ReleasableLease({ lease }: { lease: TelephonyLeaseView }) {
  const qc = useQueryClient()
  const release = useMutation({
    ...releaseLineLeaseMutation(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: listLineLeasesQueryKey() }),
  })

  return (
    <LeaseRow
      lease={lease}
      unitRef={<UnitLink id={lease.unit_id} />}
      actions={
        <>
          {(lease.status === 'registering' || lease.status === 'active') && (
            <ConfirmButton
              size="sm"
              pending={release.isPending}
              onConfirm={() =>
                release.mutate({ path: { id: lease.id }, query: { phase: 'drain' } })
              }
            >
              Слить
            </ConfirmButton>
          )}
          {lease.status === 'draining' && (
            <ConfirmButton
              size="sm"
              confirmLabel="Освободить?"
              pending={release.isPending}
              onConfirm={() =>
                release.mutate({ path: { id: lease.id }, query: { phase: 'finalize' } })
              }
            >
              Финализировать
            </ConfirmButton>
          )}
        </>
      }
      error={
        release.isError ? (
          <p className="w-full text-caption text-danger">{errorMessage(release.error)}</p>
        ) : undefined
      }
    />
  )
}
